/**
 * @description Product create/edit form with reusable explicit pricing configuration.
 * @dependencies React, chart-of-accounts API, ImageUpload, PricingConfiguration.
 * @notes Prices are entered in dollars; existing variants are retained and deactivated on mode changes.
 */

import React, { useState, useEffect } from 'react';
import ImageUpload from './ImageUpload';
import PricingConfiguration, {
  getVariantPricingError,
  pricingTypes
} from './PricingConfiguration';
import { chartOfAccountsService } from '../../services/api';

const variantFingerprint = (variantList) => JSON.stringify(variantList.map((variant) => ({
  id: variant.id || null,
  name: variant.name || '',
  amount: variant.amount !== undefined
    ? String(variant.amount)
    : (variant.amountCents !== undefined ? (variant.amountCents / 100).toFixed(2) : ''),
  isActive: variant.isActive !== false
})));

/** True when purchase cost is a non-zero amount. API maps 0 cents → "0.00" (truthy). */
const hasNonZeroPurchaseCost = (purchaseCost) => {
  const n = Number(purchaseCost);
  return Number.isFinite(n) && n !== 0;
};

/** Canonical empty string for dirty-check (0, "0.00", null, undefined, '' are equivalent). */
const purchaseCostCompareValue = (purchaseCost) => {
  if (purchaseCost === '' || purchaseCost == null) return '';
  const n = Number(purchaseCost);
  if (Number.isFinite(n) && n === 0) return '';
  return String(purchaseCost);
};

const ProductForm = ({ 
  onSubmit, 
  onCancel, 
  initialData = null, 
  categories = [], 
  vendors = [],
  onFormChange
}) => {
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    itemType: initialData?.itemType || '',
    stockQuantity: initialData?.stockQuantity || 0,
    sku: initialData?.sku || '',
    categoryId: initialData?.categoryId || '',
    description: initialData?.description || '',
    pricingType: initialData?.pricingType || pricingTypes.fixed,
    price: initialData?.price ?? '',
    incomeAccount: initialData?.incomeAccount || '',
    incomeAccountId: initialData?.incomeAccountId || '',
    salesTaxCategory: initialData?.salesTaxCategory || '',
    showVendorDetails: initialData?.showVendorDetails || false,
    purchaseDescription: initialData?.purchaseDescription || '',
    purchaseCost: hasNonZeroPurchaseCost(initialData?.purchaseCost)
      ? initialData.purchaseCost
      : '',
    expenseAccount: initialData?.expenseAccount || '',
    expenseAccountId: initialData?.expenseAccountId || '',
    inventoryAccountId: initialData?.inventoryAccountId || '',
    vendorId: initialData?.vendorId || '',
    imageData: initialData?.imageData || null,
    allowCustomAmount: initialData?.pricingType === pricingTypes.customOnly
      ? true
      : (initialData?.allowCustomAmount || false)
  });

  // Variants state — managed in-form for create; pre-loaded for edit
  const [variants, setVariants] = useState(
    (initialData?.variants || []).map((v) => ({
      id: v.id || null,
      name: v.name || '',
      amount: v.amountCents !== undefined ? (v.amountCents / 100).toFixed(2) : '',
      sortOrder: v.sortOrder !== undefined ? v.sortOrder : 0,
      isActive: v.isActive !== false,
      _key: v.id || Math.random().toString(36).slice(2)
    }))
  );

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [accounts, setAccounts] = useState({
    income: [],
    expense: [],
    inventory: []
  });
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const isEditing = !!initialData;

  // Fetch chart of accounts
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        setLoadingAccounts(true);
        
        // Fetch different account types
        const [revenueAccounts, expenseAccounts, cogsAccounts, assetAccounts] = await Promise.all([
          chartOfAccountsService.getAccountsByType('REVENUE'),
          chartOfAccountsService.getAccountsByType('EXPENSE'),
          chartOfAccountsService.getAccountsByType('COGS'),
          chartOfAccountsService.getAccountsByType('ASSET')
        ]);
        
        // Filter inventory accounts from assets
        const inventoryAccounts = Array.isArray(assetAccounts) 
          ? assetAccounts.filter(acc => acc.accountName && acc.accountName.toLowerCase().includes('inventory'))
          : [];
        
        setAccounts({
          income: revenueAccounts,
          expense: [...expenseAccounts, ...cogsAccounts], // Combine expense and COGS
          inventory: inventoryAccounts
        });
        
        // Set default values if not editing
        if (!isEditing) {
          // Default to Sales Revenue (4000)
          const defaultIncomeAccount = revenueAccounts.find(acc => acc.accountNumber === '4000');
          if (defaultIncomeAccount) {
            setFormData(prev => ({
              ...prev,
              incomeAccountId: defaultIncomeAccount.id,
              incomeAccount: `${defaultIncomeAccount.accountNumber} - ${defaultIncomeAccount.accountName}`
            }));
          }
          
          // Default to COGS (5000)
          const defaultExpenseAccount = cogsAccounts.find(acc => acc.accountNumber === '5000');
          if (defaultExpenseAccount) {
            setFormData(prev => ({
              ...prev,
              expenseAccountId: defaultExpenseAccount.id,
              expenseAccount: `${defaultExpenseAccount.accountNumber} - ${defaultExpenseAccount.accountName}`
            }));
          }
          
          // Default to Inventory (1300)
          const defaultInventoryAccount = inventoryAccounts.find(acc => acc.accountNumber === '1300');
          if (defaultInventoryAccount) {
            setFormData(prev => ({
              ...prev,
              inventoryAccountId: defaultInventoryAccount.id
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching accounts:', error);
        // Show a helpful message if accounts aren't initialized
        if (accounts.income.length === 0 && accounts.expense.length === 0) {
          alert('Your chart of accounts needs to be initialized. Please contact support or refresh the page and try again.');
        }
      } finally {
        setLoadingAccounts(false);
      }
    };
    
    fetchAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps  
  }, [isEditing]); // accounts.expense/income.length used for error handling only, should not trigger re-fetch

  // Check if form has changed from initial data
  useEffect(() => {
    if (onFormChange) {
      const formHasChanges = Object.keys(formData).some(key => {
        const initialValue = initialData?.[key];
        const currentValue = formData[key];

        // For new forms (no initialData), only consider user-entered fields as changes
        if (!initialData) {
          // Fields that are auto-populated by the system (not user input)
          // These should never trigger the "unsaved changes" warning for new products
          const autoPopulatedFields = [
            'incomeAccount',
            'incomeAccountId',
            'expenseAccount',
            'expenseAccountId',
            'inventoryAccountId',
            'stockQuantity',
            'showVendorDetails',
            'imageData',
            'pricingType'
          ];

          // Skip auto-populated fields entirely - they're not user changes
          if (autoPopulatedFields.includes(key)) {
            return false;
          }

          // For user-input fields, only consider it changed if it's not empty
          if (typeof currentValue === 'boolean') {
            return currentValue !== false;
          }
          if (typeof currentValue === 'number') {
            return currentValue !== 0;
          }
          return currentValue !== '' && currentValue !== null;
        }

        // For edit forms, compare with initial value
        if (key === 'imageData') {
          return JSON.stringify(initialValue) !== JSON.stringify(currentValue);
        }
        if (key === 'purchaseCost') {
          return purchaseCostCompareValue(initialValue)
            !== purchaseCostCompareValue(currentValue);
        }
        return initialValue !== currentValue;
      });

      const variantsHaveChanged = variantFingerprint(variants)
        !== variantFingerprint(initialData?.variants || []);
      onFormChange(formHasChanges || variantsHaveChanged);
    }
  }, [formData, variants, initialData, onFormChange]);

  // Check if vendor details should be shown on edit.
  // purchase_cost_cents defaults/clears to 0 → API purchaseCost "0.00", which is
  // truthy — treat zero/empty as "no cost" so Include Vendor Details stays off.
  useEffect(() => {
    if (isEditing && initialData) {
      const hasVendorDetails = !!(
        initialData.vendorId ||
        initialData.purchaseDescription ||
        hasNonZeroPurchaseCost(initialData.purchaseCost) ||
        initialData.expenseAccount ||
        initialData.expenseAccountId
      );
      setFormData(prev => ({ ...prev, showVendorDetails: hasVendorDetails }));
    }
  }, [isEditing, initialData]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Product name is required';
    }
    
    if (!formData.itemType) {
      newErrors.itemType = 'Product type is required';
    }
    
    if (
      formData.pricingType === pricingTypes.fixed
      && (formData.price === '' || Number.isNaN(Number(formData.price)) || Number(formData.price) < 0)
    ) {
      newErrors.price = 'Valid price is required';
    }

    if (formData.pricingType === pricingTypes.variant) {
      newErrors.variants = getVariantPricingError(variants);
      if (!newErrors.variants) delete newErrors.variants;
    }
    
    if (!formData.incomeAccountId) {
      newErrors.incomeAccountId = 'Income account is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      setIsSubmitting(true);
      
      // Clean up vendor details if not shown (but preserve vendorId)
      const dataToSubmit = { ...formData };
      if (!formData.showVendorDetails) {
        dataToSubmit.purchaseDescription = '';
        dataToSubmit.purchaseCost = null;
        dataToSubmit.expenseAccount = '';
        dataToSubmit.expenseAccountId = null;
        // Keep vendorId - a product can have a vendor without purchase details
      }

      // Optional integer FKs: empty select values must be null, not ""
      // (Postgres rejects invalid input syntax for type integer: "")
      for (const field of [
        'categoryId',
        'expenseAccountId',
        'inventoryAccountId',
        'vendorId',
        'incomeAccountId'
      ]) {
        if (dataToSubmit[field] === '' || dataToSubmit[field] === undefined) {
          dataToSubmit[field] = null;
        }
      }
      // Keep null (do not omit) so update clears purchase_cost_cents to 0
      if (dataToSubmit.purchaseCost === '') {
        dataToSubmit.purchaseCost = null;
      }
      
      // Remove UI-only field
      delete dataToSubmit.showVendorDetails;
      if (formData.pricingType === pricingTypes.customOnly) {
        dataToSubmit.allowCustomAmount = true;
      }

      // Attach variants (normalized to the API shape)
      dataToSubmit.variants = variants
        .filter((v) => v.name.trim())
        .map((v, idx) => ({
          id: v.id || undefined,
          name: v.name.trim(),
          amount: parseFloat(v.amount) || 0,
          sortOrder: idx,
          isActive: formData.pricingType === pricingTypes.variant
            ? v.isActive !== false
            : false
        }));
      
      await onSubmit(dataToSubmit);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
    
    // Auto-set inventory account when item type is changed to Inventory Item
    if (name === 'itemType' && value === 'Inventory Item') {
      const defaultInventoryAccount = accounts.inventory.find(acc => acc.accountNumber === '1300');
      if (defaultInventoryAccount) {
        setFormData(prev => ({
          ...prev,
          inventoryAccountId: defaultInventoryAccount.id
        }));
      }
    } else if (name === 'itemType' && value !== 'Inventory Item') {
      // Clear inventory account if not an inventory item
      setFormData(prev => ({
        ...prev,
        inventoryAccountId: ''
      }));
    }
  };

  const handleAccountChange = (accountType, value) => {
    if (!value) {
      setFormData(prev => ({
        ...prev,
        [`${accountType}AccountId`]: '',
        [`${accountType}Account`]: ''
      }));
      return;
    }
    
    const accountId = parseInt(value);
    const accountList = accounts[accountType];
    const selectedAccount = accountList.find(acc => acc.id === accountId);
    
    if (selectedAccount) {
      setFormData(prev => ({
        ...prev,
        [`${accountType}AccountId`]: accountId,
        [`${accountType}Account`]: `${selectedAccount.accountNumber} - ${selectedAccount.accountName}`
      }));
    }
  };

  const handleImageReady = (imageData) => {
    setFormData(prev => ({ ...prev, imageData }));
  };

  const handlePricingTypeChange = (pricingType) => {
    setFormData((prev) => ({
      ...prev,
      pricingType,
      allowCustomAmount: pricingType === pricingTypes.customOnly
        ? true
        : prev.allowCustomAmount
    }));
    setErrors((prev) => ({ ...prev, price: null, variants: null }));
  };

  const deactivateVariants = () => {
    setVariants((prev) => prev.map((variant) => ({ ...variant, isActive: false })));
  };

  // ─── Variant helpers ───────────────────────────────────────────────────────
  const addVariant = () => {
    setVariants(prev => [
      ...prev,
      { id: null, name: '', amount: '', sortOrder: prev.length, isActive: true, _key: Math.random().toString(36).slice(2) }
    ]);
  };

  const updateVariantField = (key, field, value) => {
    setVariants(prev => prev.map(v => v._key === key ? { ...v, [field]: value } : v));
  };

  const removeVariant = (key) => {
    setVariants(prev => prev.filter(v => v._key !== key));
  };

  if (loadingAccounts) {
    return (
      <div className="space-y-6 min-h-[500px]">
        {/* Skeleton loader that matches form layout */}
        <div className="flex justify-center">
          <div className="w-32 h-32 bg-gray-100 rounded-lg animate-pulse" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className={i === 4 ? 'md:col-span-2' : ''}>
              <div className="h-4 w-24 bg-gray-200 rounded mb-2 animate-pulse" />
              <div className="h-10 bg-gray-100 rounded animate-pulse" />
            </div>
          ))}
        </div>
        <div className="flex justify-center pt-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand" />
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Image Upload */}
      <ImageUpload
        imageData={formData.imageData}
        onImageReady={handleImageReady}
      />

      {/* Product Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Product Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-brand ${
              errors.name ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
        </div>

        <div>
          <label htmlFor="itemType" className="block text-sm font-medium text-gray-700 mb-1">
            Product Type <span className="text-red-500">*</span>
          </label>
          <select
            id="itemType"
            name="itemType"
            value={formData.itemType}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-brand ${
              errors.itemType ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Select Type</option>
            <option value="Inventory Item">Inventory Item</option>
            <option value="Non-Inventory Item">Non-Inventory Item</option>
            <option value="Service">Service</option>
          </select>
          {errors.itemType && <p className="text-red-500 text-sm mt-1">{errors.itemType}</p>}
        </div>

        {formData.itemType === 'Inventory Item' && (
        <div>
        <label htmlFor="stockQuantity" className="block text-sm font-medium text-gray-700 mb-1">
          Stock Quantity
        </label>
        <input
          type="number"
          id="stockQuantity"
          name="stockQuantity"
          value={formData.stockQuantity}
          onChange={handleChange}
          min="0"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand"
        />
      </div>
      )}

        <div>
          <label htmlFor="sku" className="block text-sm font-medium text-gray-700 mb-1">
            SKU
          </label>
          <input
            type="text"
            id="sku"
            name="sku"
            value={formData.sku}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand"
          />
        </div>

        <div>
          <label htmlFor="categoryId" className="block text-sm font-medium text-gray-700 mb-1">
            Category
          </label>
          <select
            id="categoryId"
            name="categoryId"
            value={formData.categoryId}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand"
          >
            <option value="">Select Category</option>
            {categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        <div className="md:col-span-2">
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows="3"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand"
          />
        </div>

        <PricingConfiguration
          pricingType={formData.pricingType}
          price={formData.price}
          allowCustomAmount={formData.allowCustomAmount}
          variants={variants}
          errors={errors}
          onPricingTypeChange={handlePricingTypeChange}
          onPriceChange={handleChange}
          onAllowCustomAmountChange={handleChange}
          onDeactivateVariants={deactivateVariants}
          onAddVariant={addVariant}
          onUpdateVariant={updateVariantField}
          onRemoveVariant={removeVariant}
        />

        <div>
          <label htmlFor="incomeAccountId" className="block text-sm font-medium text-gray-700 mb-1">
            Income Account <span className="text-red-500">*</span>
          </label>
          <select
            id="incomeAccountId"
            name="incomeAccountId"
            value={formData.incomeAccountId}
            onChange={(e) => handleAccountChange('income', e.target.value)}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-brand ${
              errors.incomeAccountId ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Select Account</option>
            {accounts.income.map(account => (
              <option key={account.id} value={account.id}>
                {account.accountNumber} - {account.accountName}
              </option>
            ))}
          </select>
          {errors.incomeAccountId && <p className="text-red-500 text-sm mt-1">{errors.incomeAccountId}</p>}
        </div>

      </div>

      {/* Vendor Details Section */}
      <div className="border-t pt-4">
        <label className="flex items-center mb-4">
          <input
            type="checkbox"
            name="showVendorDetails"
            checked={formData.showVendorDetails}
            onChange={handleChange}
            className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded"
          />
          <span className="ml-2 text-sm font-medium text-gray-700">Include Vendor Details</span>
        </label>

        {formData.showVendorDetails && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="purchaseDescription" className="block text-sm font-medium text-gray-700 mb-1">
                Purchase Description
              </label>
              <input
                type="text"
                id="purchaseDescription"
                name="purchaseDescription"
                value={formData.purchaseDescription}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand"
              />
            </div>

            <div>
              <label htmlFor="purchaseCost" className="block text-sm font-medium text-gray-700 mb-1">
                Purchase Cost
              </label>
              <input
                type="number"
                id="purchaseCost"
                name="purchaseCost"
                value={formData.purchaseCost}
                onChange={handleChange}
                step="0.01"
                placeholder="0.00"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand"
              />
            </div>

            <div>
              <label htmlFor="expenseAccountId" className="block text-sm font-medium text-gray-700 mb-1">
                Expense Account
              </label>
              <select
                id="expenseAccountId"
                name="expenseAccountId"
                value={formData.expenseAccountId}
                onChange={(e) => handleAccountChange('expense', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand"
              >
                <option value="">Select Account</option>
                {accounts.expense.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.accountNumber} - {account.accountName}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="vendorId" className="block text-sm font-medium text-gray-700 mb-1">
                Vendor
              </label>
              <select
                id="vendorId"
                name="vendorId"
                value={formData.vendorId}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand"
              >
                <option value="">Select Vendor</option>
                {vendors.map(vendor => (
                  <option key={vendor.id} value={vendor.id}>
                    {vendor.vendorName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-end space-x-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 bg-brand text-white rounded-md hover:bg-brand-hover disabled:opacity-50 font-semibold transition-colors"
        >
          {isSubmitting ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Update Product' : 'Create Product')}
        </button>
      </div>
    </form>
  );
};

export default ProductForm;